// Looks up a freshly-picked SAB client and projects it into the persisted
// global selection shape. Done here (rather than in the modal) so the modal
// stays caller-agnostic — pages that just want a one-shot selection don't
// need a displayName, while the global header chip does.
import {
	type SabClientListItemDto,
	SabClientType,
} from "Infrastructure/Api/generated/types.gen";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import {
	LaunchModeModal,
	type LaunchModeSelection,
} from "Modeling/Components/LaunchModeModal";
import { User, X } from "Shared/appIcons";
import { useSelectedClient } from "Shared/Client/SelectedClientContext";
import { clientDisplayName } from "Shared/Client/selectedClientStorage";
import { useState } from "react";
import { Button } from "@/components/ui/button";

export const ClientHeaderSwitcher = () => {
	const { t } = useResource();
	const { client, setClient, clearClient } = useSelectedClient();
	const [open, setOpen] = useState(false);
	// The picker inside LaunchModeModal returns a SabClientListItemDto via its
	// own callback; the modal then forwards only the ids. To capture the
	// displayName we listen on the same modal but reach into the picker's
	// onPick by passing a wrapper handler — implemented via the
	// `onPickRaw` extension below in LaunchModeModal.
	const [lastPicked, setLastPicked] = useState<SabClientListItemDto | null>(
		null,
	);

	const handleLaunch = (selection: LaunchModeSelection) => {
		if (selection.mode === "without-client") {
			clearClient();
			setOpen(false);
			return;
		}
		// Prefer the captured row (has the full name + type); fall back to the
		// identification number when, for some reason, the row wasn't captured.
		const capturedRow =
			lastPicked !== null && lastPicked.sabClientID === selection.sabClientID
				? lastPicked
				: null;
		const displayName =
			capturedRow !== null
				? clientDisplayName(capturedRow)
				: selection.sabIdentificationNumber;
		setClient({
			sabClientID: selection.sabClientID,
			sabIdentificationNumber: selection.sabIdentificationNumber,
			displayName,
			clientType: capturedRow?.type ?? SabClientType.PhysicalPerson,
		});
		setLastPicked(null);
		setOpen(false);
	};

	const label =
		client !== null
			? `${t(Resources.Modeling.LaunchMode.Switcher.SelectedPrefix)}: ${client.displayName}`
			: t(Resources.Modeling.LaunchMode.Switcher.NoClient);

	return (
		<>
			<div className="inline-flex items-center gap-1.5">
				<Button
					type="button"
					variant="outline"
					size="sm"
					onClick={() => setOpen(true)}
					className="h-7 gap-1.5 rounded-full px-2.5"
				>
					<span className="bg-primary/10 text-primary flex size-4 flex-shrink-0 items-center justify-center rounded-full">
						<User className="size-3" />
					</span>
					<span className="text-tiny font-medium">{label}</span>
					{client !== null ? (
						<span className="text-tiny text-muted-foreground">
							· {t(Resources.Modeling.LaunchMode.Switcher.Change)}
						</span>
					) : null}
				</Button>
				{client !== null ? (
					<Button
						type="button"
						variant="ghost"
						size="icon-sm"
						onClick={() => clearClient()}
						className="text-muted-foreground hover:text-foreground size-7"
						title={t(Resources.Modeling.LaunchMode.Switcher.Clear)}
						aria-label={t(Resources.Modeling.LaunchMode.Switcher.Clear)}
					>
						<X className="size-3.5" />
					</Button>
				) : null}
			</div>
			<LaunchModeModal
				open={open}
				onOpenChange={setOpen}
				onLaunch={handleLaunch}
				onPickClient={setLastPicked}
				title={t(Resources.Modeling.LaunchMode.Switcher.Title)}
				description={t(Resources.Modeling.LaunchMode.Description)}
			/>
		</>
	);
};
